import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Download, X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Subtle in-app "Update available" banner. Only renders inside the Job Ticker
 * Android APK (detected via window.JTBridge). Compares the APK's build
 * versionName against the server's advertised latest_version. Dismiss is
 * per-session so users still see the nudge next app launch.
 *
 * When the user taps "Update now" and the native bridge exposes
 * `downloadUpdate`, we render an inline "Downloading…" state with live
 * percentage (fed by window.__jtDownload* callbacks the native side fires).
 * Non-Android previews fall back to opening the URL in a new tab.
 */

function parseVersion(v) {
  if (!v) return [0, 0, 0];
  return String(v).split(".").map(n => parseInt(n, 10) || 0);
}

function isNewer(a, b) {
  const av = parseVersion(a);
  const bv = parseVersion(b);
  for (let i = 0; i < Math.max(av.length, bv.length); i++) {
    const x = av[i] || 0, y = bv[i] || 0;
    if (x > y) return true;
    if (x < y) return false;
  }
  return false;
}

const DISMISS_KEY = "jt_update_banner_dismissed_for";

export default function UpdateBanner() {
  const [info, setInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | downloading | installing | error
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const mountedRef = useRef(true);

  useEffect(() => {
    const bridge = typeof window !== "undefined" ? window.JTBridge : null;
    if (!bridge || typeof bridge.getAppVersion !== "function") return;
    const currentVersion = bridge.getAppVersion();
    if (!currentVersion) return;

    const url = `${process.env.REACT_APP_BACKEND_URL}/api/app/version`;
    axios.get(url).then(({ data }) => {
      if (!data?.latest_version || !isNewer(data.latest_version, currentVersion)) return;
      const skipFor = sessionStorage.getItem(DISMISS_KEY);
      if (skipFor === data.latest_version) { setDismissed(true); }
      setInfo({
        latest_version: data.latest_version,
        current_version: currentVersion,
        download_url: data.download_url,
        changelog: data.changelog || "",
      });
    }).catch(() => { /* silent — no banner if we can't reach server */ });
  }, []);

  // Wire the native progress callbacks. Native calls window.__jtDownload*(...)
  // from the WebView; we translate those into React state updates.
  useEffect(() => {
    mountedRef.current = true;
    window.__jtDownloadStart = () => {
      if (!mountedRef.current) return;
      setPhase("downloading"); setProgress(0); setErrorMsg("");
    };
    window.__jtDownloadProgress = (pct) => {
      if (!mountedRef.current) return;
      const n = typeof pct === "number" ? pct : parseInt(pct, 10);
      if (!Number.isNaN(n)) setProgress(Math.max(0, Math.min(100, n)));
    };
    window.__jtDownloadComplete = () => {
      if (!mountedRef.current) return;
      setPhase("installing"); setProgress(100);
    };
    window.__jtDownloadError = (msg) => {
      if (!mountedRef.current) return;
      setPhase("error");
      setErrorMsg(typeof msg === "string" ? msg : "Download failed");
    };
    return () => {
      mountedRef.current = false;
      try {
        delete window.__jtDownloadStart;
        delete window.__jtDownloadProgress;
        delete window.__jtDownloadComplete;
        delete window.__jtDownloadError;
      } catch (_) { /* ignore */ }
    };
  }, []);

  if (!info || dismissed) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, info.latest_version);
    setDismissed(true);
  };

  const startDownload = () => {
    const bridge = window.JTBridge;
    if (bridge && typeof bridge.downloadUpdate === "function") {
      setPhase("downloading"); setProgress(0); setErrorMsg("");
      try { bridge.downloadUpdate(info.download_url || ""); }
      catch (e) { setPhase("error"); setErrorMsg(e?.message || "Bridge error"); }
      return;
    }
    // Browser / older APK fallback — open in system browser
    if (info.download_url) window.open(info.download_url, "_blank");
  };

  const busy = phase === "downloading" || phase === "installing";

  return (
    <div
      data-testid="update-banner"
      className="bg-indigo-600 text-white px-4 py-2 flex items-center gap-3 shadow-sm"
    >
      <div className="w-7 h-7 rounded-md bg-white/15 grid place-items-center shrink-0">
        {phase === "downloading" || phase === "installing" ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : phase === "error" ? (
          <AlertCircle className="w-3.5 h-3.5" />
        ) : (
          <Download className="w-3.5 h-3.5" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        {phase === "idle" && (
          <>
            <div className="text-xs sm:text-sm font-semibold leading-tight">
              Update available · v{info.latest_version}
            </div>
            <div className="text-[10px] sm:text-xs text-white/80 truncate leading-tight">
              You're on v{info.current_version}{info.changelog ? ` — ${info.changelog}` : ""}
            </div>
          </>
        )}

        {phase === "downloading" && (
          <>
            <div
              data-testid="update-banner-status"
              className="text-xs sm:text-sm font-semibold leading-tight flex items-center gap-2"
            >
              <span>Downloading… {progress}%</span>
            </div>
            <div className="mt-1 h-1 bg-white/20 rounded-full overflow-hidden">
              <div
                data-testid="update-banner-progress"
                className="h-full bg-white transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </>
        )}

        {phase === "installing" && (
          <div
            data-testid="update-banner-status"
            className="text-xs sm:text-sm font-semibold leading-tight flex items-center gap-2"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            <span>Ready — opening installer…</span>
          </div>
        )}

        {phase === "error" && (
          <>
            <div
              data-testid="update-banner-status"
              className="text-xs sm:text-sm font-semibold leading-tight"
            >
              Update failed
            </div>
            <div className="text-[10px] sm:text-xs text-white/80 truncate leading-tight">
              {errorMsg || "Please try again."}
            </div>
          </>
        )}
      </div>

      {phase === "idle" && (
        <button
          data-testid="update-banner-install"
          onClick={startDownload}
          className="shrink-0 rounded-md bg-white text-indigo-700 text-xs font-semibold px-3 py-1.5 hover:bg-indigo-50 transition-colors"
        >
          Update now
        </button>
      )}

      {phase === "error" && (
        <button
          data-testid="update-banner-retry"
          onClick={startDownload}
          className="shrink-0 rounded-md bg-white text-indigo-700 text-xs font-semibold px-3 py-1.5 hover:bg-indigo-50 transition-colors"
        >
          Retry
        </button>
      )}

      <button
        data-testid="update-banner-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        disabled={busy}
        className="shrink-0 p-1 rounded-md hover:bg-white/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
